/* Shopify Related Products transport only; no independent ranking.
 * Missing/failed retrieval is never a successful zero-result response.
 * 2026-09-03 / kaho + Codex.
 */
(() => {
  function requestUrl(dataset, base) {
    const url = new URL(dataset.url, base);
    if (url.origin !== new URL(base).origin || !dataset.productId || !dataset.sectionId) throw new Error('invalid_request');
    url.searchParams.set('product_id', dataset.productId);
    url.searchParams.set('section_id', dataset.sectionId);
    return url.toString();
  }

  function validateResponse(source, expected) {
    if (!source || source.dataset.fdpRecommendationContract !== '1' ||
      source.dataset.productId !== expected.productId || source.dataset.sectionId !== expected.sectionId) throw new Error('identity_mismatch');
    if (source.dataset.fdpState !== 'ready') throw new Error('data_unresolved');
    if (!/^\d+$/.test(source.dataset.fdpEligibleCount || '')) throw new Error('invalid_count');
    const count = Number(source.dataset.fdpEligibleCount);
    const cards = [...source.querySelectorAll('[data-fdp-recommendation-product-id]')];
    const ids = cards.map(card => card.dataset.fdpRecommendationProductId);
    const placeholders = source.querySelectorAll('.fdp-recommendation-preview').length;
    if (!source.querySelector('[data-fdp-recommendation-body]') || count > 10 || cards.length !== count ||
      ids.some(id => !id || id === expected.productId) || new Set(ids).size !== ids.length) throw new Error('invalid_cards');
    if (count === 0) {
      if (!['no_candidates', 'all_excluded'].includes(source.dataset.fdpEmptyReason) || placeholders !== 4) throw new Error('invalid_empty');
    } else if (source.dataset.fdpEmptyReason || placeholders) throw new Error('invalid_empty');
    return source;
  }

  function elementClass(env) {
    return class FdpProductRecommendations extends env.HTMLElement {
      connectedCallback() {
        this.retryListener = event => {
          if (event.target.closest?.('[data-fdp-recommendation-retry]')) this.loadRecommendations();
        };
        this.addEventListener('click', this.retryListener);
        if (this.dataset.fdpState !== 'pending') return;
        if (!env.IntersectionObserver) return this.loadRecommendations();
        this.observer = new env.IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.loadRecommendations();
        }, {rootMargin: '0px 0px 400px 0px'});
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.requestToken = (this.requestToken || 0) + 1;
        this.controller?.abort();
        env.clearTimeout(this.timeout);
        this.removeEventListener('click', this.retryListener);
        if (this.dataset.fdpState === 'loading') this.showState('pending');
      }

      showState(state, reason = '') {
        this.dataset.fdpState = state;
        this.dataset.fdpErrorReason = reason;
        this.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
        const loading = this.querySelector('[data-fdp-recommendation-loading]');
        const error = this.querySelector('[data-fdp-recommendation-error]');
        if (loading) loading.hidden = !['pending', 'loading'].includes(state);
        if (error) error.hidden = state !== 'error';
      }

      initializeRecommendations(productId) {
        this.dataset.productId = String(productId);
        this.observer?.disconnect();
        return this.loadRecommendations();
      }

      async loadRecommendations() {
        this.controller?.abort();
        env.clearTimeout(this.timeout);
        const token = this.requestToken = (this.requestToken || 0) + 1;
        const controller = this.controller = new env.AbortController();
        const expected = {productId: this.dataset.productId, sectionId: this.dataset.sectionId};
        let timedOut = false;
        this.showState('loading');
        this.dataset.fdpEligibleCount = '0';
        this.dataset.fdpEmptyReason = '';
        this.querySelector('[data-fdp-recommendation-body]')?.replaceChildren();
        const timeout = this.timeout = env.setTimeout(() => { timedOut = true; controller.abort(); }, 10000);
        try {
          const url = requestUrl(this.dataset, env.location.href);
          const response = await env.fetch(url, {signal: controller.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('http_error');
          const html = new env.DOMParser().parseFromString(await response.text(), 'text/html');
          const sections = html.querySelectorAll('fdp-product-recommendations');
          if (sections.length !== 1) throw new Error('invalid_response');
          const source = validateResponse(sections[0], expected);
          if (!this.isConnected || token !== this.requestToken) return;
          this.innerHTML = source.innerHTML;
          for (const key of ['fdpEligibleCount', 'fdpEmptyReason', 'fdpExcludedCount', 'fdpInvalidCount']) this.dataset[key] = source.dataset[key];
          this.classList.add('product-recommendations--loaded');
          this.showState('ready');
        } catch (error) {
          if (!this.isConnected || token !== this.requestToken || (controller.signal.aborted && !timedOut)) return;
          const reasons = ['invalid_request', 'identity_mismatch', 'data_unresolved', 'invalid_count', 'invalid_cards', 'invalid_empty', 'http_error', 'invalid_response'];
          this.showState('error', timedOut ? 'timeout' : reasons.includes(error.message) ? error.message : 'network_error');
        } finally {
          env.clearTimeout(timeout);
        }
      }
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = {requestUrl, validateResponse, elementClass};
  else if (!customElements.get('fdp-product-recommendations')) customElements.define('fdp-product-recommendations', elementClass(window));
})();
